import { configFrom, isMainLift, liftTimeline, planWorkout, startSession, timingFrom, type MainLift, type ProgrammeState, type RepwiseEvent } from '@repwise/domain';
import { uuidv7 } from '@repwise/sync';
import { useState } from 'react';
import { Link } from 'react-router-dom';
import { PlanReady, SwapSheet } from '../components/PlanReady.tsx';
import { useCatalogue } from '../lib/plan.ts';
import { Button, Card, SyncPill, TabHeader } from '../components/ui.tsx';
import { useHostedPlan } from '../lib/plan.ts';
import { reasonChip, wt } from '../lib/copy.ts';
import { LIFT_NAME } from '../lib/defaults.ts';
import { useRepo, useSyncStatus } from '../lib/hooks.ts';
import { NextDate } from '../components/NextDate.tsx';
import { WeightButton } from '../components/PlateSheet.tsx';
import { WeightEdit } from '../components/WeightEdit.tsx';

/** S01: today's workout, each target with its reason; deload and break offers inline (C14/C15). */
export function Ready({ programme, events }: { programme: ProgrammeState; events: RepwiseEvent[] }) {
  const hosted = useHostedPlan(events, programme);
  if (hosted.selection) {
    if (!hosted.mode || (hosted.mode.kind === 'hosted' && hosted.mode.status !== 'needs-download' && !hosted.live)) return <main className="p-5 text-text-muted">Loading…</main>;
    return <PlanReady programme={programme} events={events} hosted={hosted} />;
  }
  return <BuiltInReady programme={programme} events={events} />;
}

function BuiltInReady({ programme, events }: { programme: ProgrammeState; events: RepwiseEvent[] }) {
  const { store } = useRepo();
  const status = useSyncStatus();
  const now = Date.now();
  const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;
  const plan = planWorkout(programme, now, undefined, tz); // the user's settings and local calendar days
  const [swapping, setSwapping] = useState<MainLift | null>(null);
  const catalogue = useCatalogue();
  const u = programme.settings.unit;
  const kg = (m: number | null | undefined) => wt(m, u);
  const append = (e: RepwiseEvent[]) => void store.append(e);

  const start = () => {
    const { events } = startSession({ id: uuidv7(), workoutId: uuidv7(), kind: plan.kind, at: Date.now(), prescriptions: plan.prescriptions, config: configFrom(programme.settings), timing: timingFrom(programme.settings) });
    append(events);
  };

  return (
    <main className="p-5 pb-40 max-w-[480px] mx-auto">
      <TabHeader title={`Workout ${plan.kind}`}>
        <SyncPill status={status} />
      </TabHeader>
      <p className="text-text-muted">{new Date(now).toLocaleDateString('en-GB', { weekday: 'short', day: 'numeric', month: 'short' })} · About 50 min</p>
      <NextDate events={events} daysPerWeek={3} workout={plan.kind} />

      {plan.breakOffer > 0 && (
        <Card className="mt-5">
          <p>
            It's been a while since your last workout. Ease back in {Math.round(plan.breakOffer * 100)}% lighter today?
          </p>
          <div className="flex gap-3 mt-3">
            <Button kind="secondary" onClick={() => append([{ type: 'break_restart_accepted', id: uuidv7(), at: Date.now(), fraction: plan.breakOffer }])}>
              Start lighter
            </Button>
            <Button kind="text" onClick={() => append([{ type: 'break_restart_skipped', id: uuidv7(), at: Date.now() }])}>
              Use usual weights
            </Button>
          </div>
        </Card>
      )}

      {/* S01b `_builtin_5x5` final (Wave 2.4 batch 1): deload offers first, then a card per lift */}
      {plan.prescriptions.map((p) => {
        const lift = (p.swappedFrom ?? p.exercise) as MainLift;
        const offer = plan.offers[lift];
        if (!offer) return null;
        return (
          <Card key={`offer-${lift}`} className="mt-5">
            <h2 className="text-headline">Deload suggested</h2>
            <p className="text-sm text-text-muted mt-1">
              {LIFT_NAME[lift]}: missed {programme.lifts[lift].state.misses} times in a row at {kg(p.weight)}. Drop {Math.round(offer.fraction * 1000) / 10}% to {kg(offer.to)} and build back?
            </p>
            {!programme.settings.lifts[lift].scheme && liftTimeline(events, lift).some((r) => r.what === 'deload') && (
              <p className="text-xs text-text-muted mt-1">
                Stalled here before? You can also switch {LIFT_NAME[lift]} to {lift === 'deadlift' ? '1×3' : '3×5'} in{' '}
                <Link to="/settings/plan" className="underline">
                  Your plan
                </Link>
                .
              </p>
            )}
            <div className="grid grid-cols-2 gap-3 mt-3">
              <Button kind="secondary" onClick={() => append([{ type: 'deload_accepted', id: uuidv7(), at: Date.now(), lift }])}>
                Start at {kg(offer.to)}
              </Button>
              <Button kind="secondary" onClick={() => append([{ type: 'deload_declined', id: uuidv7(), at: Date.now(), lift }])}>
                Keep {kg(p.weight)}
              </Button>
            </div>
          </Card>
        );
      })}

      <ul className="mt-5 space-y-3">
        {plan.prescriptions.map((p) => {
          const lift = (p.swappedFrom ?? p.exercise) as MainLift;
          const inc = programme.settings.lifts[lift]?.increment;
          return (
            <li key={p.exercise}>
              <Card>
                <div className="flex items-start justify-between gap-2">
                  <div>
                    <h2 className="text-headline num">{p.name ?? LIFT_NAME[p.exercise]}</h2>
                    <span className={`inline-block mt-1 text-xs rounded-full px-2 py-0.5 ${p.reason === 'DELOAD_OFFER' ? 'bg-surface-sunken text-miss' : p.reason === 'INC_SUCCESS' ? 'bg-surface-sunken text-success' : 'bg-surface-sunken text-text-muted'}`}>{reasonChip(p.reason, inc)}</span>
                  </div>
                  {catalogue && isMainLift(lift) && !programme.inProgress.length && (
                    <button type="button" className="text-sm rounded-[10px] bg-surface-sunken min-h-11 px-3 shrink-0" aria-label={`Swap ${p.name ?? LIFT_NAME[p.exercise]}`} onClick={() => setSwapping(lift)}>
                      ⇄ Swap
                    </button>
                  )}
                </div>
                {p.swappedFrom && (
                  <p className="text-xs text-text-muted">
                    Instead of {LIFT_NAME[p.swappedFrom]} · {programme.swaps?.[lift]?.scope === 'next' ? 'just this workout' : 'from now on'}{' '}
                    <button type="button" className="text-now-ink underline min-h-11 px-1" onClick={() => void append([{ type: 'exercise_swapped', id: uuidv7(), at: Date.now(), selectionId: 'builtin', track: lift, to: lift, scope: 'ongoing' }])}>
                      Undo swap
                    </button>
                  </p>
                )}
                {swapping === lift && catalogue && (
                  <SwapSheet
                    p={p}
                    unit={programme.settings.unit}
                    catalogue={catalogue}
                    onCancel={() => setSwapping(null)}
                    onSwap={(to, scope, ratio) => {
                      setSwapping(null);
                      const c = catalogue.exercises.find((e) => e.id === to);
                      const equipment = c?.bodyweight ? 'bodyweight' : c?.equipment.includes('barbell') ? 'barbell' : c?.equipment.includes('dumbbells') ? 'dumbbells' : 'other';
                      void append([{ type: 'exercise_swapped', id: uuidv7(), at: Date.now(), selectionId: 'builtin', track: lift, to, scope, ...(ratio !== 1 ? { ratio } : {}), ...(c ? { name: c.name } : {}), equipment }]);
                    }}
                  />
                )}
                <div className="flex items-end justify-between mt-3 bg-surface-sunken rounded-[12px] px-3 py-2">
                  <span>
                    <span className="block text-xs text-text-muted">Target reps</span>
                    <span className="num text-num-l">
                      {p.sets} × {p.reps}
                    </span>
                  </span>
                  <span className="text-right">
                    <span className="block text-xs text-text-muted">{p.weight === null ? 'Bodyweight' : isMainLift(p.exercise) ? 'Barbell load' : 'Load'}</span>
                    {p.weight === null ? null : isMainLift(p.exercise) ? <WeightButton gym={programme.settings.gym} weight={p.weight}><span className="num text-num-l">{kg(p.weight)}</span></WeightButton> : <span className="num text-num-l">{kg(p.weight)}</span>}
                  </span>
                </div>
                {isMainLift(p.exercise) && p.weight !== null && !p.swappedFrom && !programme.inProgress.length && (
                  <WeightEdit name={LIFT_NAME[p.exercise] ?? p.exercise} weight={p.weight} unit={u} min={programme.settings.gym.bar} onSave={(w) => append([{ type: 'target_overridden', id: uuidv7(), at: Date.now(), lift: p.exercise as MainLift, weight: w }])} />
                )}
              </Card>
            </li>
          );
        })}
      </ul>

      <div className="mt-4 space-y-2">
        <Link to="/settings/plan" className="flex items-center justify-center rounded-[14px] bg-surface-raised min-h-12">
          Adjust plan
        </Link>
        <button type="button" className="w-full rounded-[14px] bg-surface-raised min-h-12" onClick={() => append([{ type: 'next_workout_set', id: uuidv7(), at: Date.now(), kind: plan.kind === 'A' ? 'B' : 'A' }])}>
          Do Workout {plan.kind === 'A' ? 'B' : 'A'} instead
        </button>
      </div>

      <div className="fixed inset-x-0 p-5 bg-surface" style={{ bottom: 'var(--tabbar-h, calc(4rem + env(safe-area-inset-bottom)))' }}>
        <div className="max-w-[480px] mx-auto">
          <Button onClick={start}>Start workout {plan.kind}</Button>
        </div>
      </div>
    </main>
  );
}
